/**
 * LandingView - Public landing page for Piccard
 *
 * One story: YouTube → cards, extension-first (v11). Hero = the promise +
 * real-flow video (landscape loop + two phone clips), how = the closed loop in
 * three moves (watch → tap and keep → review), pricing = money, FAQ = the
 * objections. Copy frozen in docs/plans/landing-v11.md.
 */

import { ArrowRight, ExternalLink } from 'lucide-react'
import { useEffect } from 'react'
import { useScrollReveal } from '../../hooks/useScrollReveal'
import { captureRefFromUrl } from '../../lib/referral-client'
import { LandingI18nProvider } from './LandingI18n'
import { useLandingT, usePublicHref } from './landing-locale'
import { CWS_URL } from './ExtensionPromo'
import { Navbar } from './Navbar'
import { Hero } from './Hero'
import { HowItWorks } from './HowItWorks'
import { OwnMaterial } from './OwnMaterial'
import { Pricing } from './Pricing'
import { FAQ } from './FAQ'
import { Footer } from './Footer'

export function LandingView() {
  // Funnel-top: capture ?ref= on landing, persist + log click.
  useEffect(() => {
    captureRefFromUrl()
  }, [])

  return (
    <LandingI18nProvider>
      <LandingPage />
    </LandingI18nProvider>
  )
}

// v11: the page body is its own component INSIDE the provider — a component
// can't consume a context it renders, so the old single-component shape left
// proof/finalCta strings permanently English on /ko/ and /zh-TW/.
function LandingPage() {
  const finalCtaRef = useScrollReveal()
  const t = useLandingT()
  const href = usePublicHref()

  return (
    <div className="min-h-screen bg-surface">
      <Navbar />
      <main>
        <Hero />

        <HowItWorks />

        {/* persona-2 surface (landing-v12 P1-2): blog traffic from exam/
            list clusters converts on "bring your own material", not on the
            extension story. */}
        <OwnMaterial />

        <Pricing />
        <FAQ />

        {/* Final CTA — oxblood gradient (the one earned gradient; the
            conversion moment deserves emphasis). v11: extension-first —
            CWS install is the primary exit, signup secondary. */}
        <div ref={finalCtaRef} className="bg-gradient-to-br from-primary to-[#4a1828] py-16 md:py-24">
          <div className="reveal max-w-3xl mx-auto px-4 text-center sm:px-6 lg:px-8">
            <h2 className="mb-4 text-balance font-headline text-3xl font-bold text-on-primary sm:text-4xl">
              {t('landing.finalCta.title')}
            </h2>
            <p className="mb-8 text-lg text-on-primary/80">
              {t('landing.finalCta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={CWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full bg-surface px-8 py-4 text-lg font-bold text-on-surface shadow-card transition-colors hover:bg-canvas focus-visible:outline-2 focus-visible:outline-on-primary focus-visible:outline-offset-2"
              >
                {t('landing.finalCta.ctaExtension')}
                <ExternalLink className="ml-2 h-5 w-5" />
              </a>
              <a
                href={href('/login')}
                className="inline-flex items-center rounded-full border-2 border-on-primary/40 px-7 py-4 text-lg font-bold text-on-primary transition-colors hover:bg-on-primary/10 focus-visible:outline-2 focus-visible:outline-on-primary focus-visible:outline-offset-2"
              >
                {t('landing.finalCta.cta')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
            <p className="mt-3 text-xs text-on-primary/70">{t('landing.finalCta.note')}</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
